import {
  useCopilotAction,
  useCopilotReadable,
  useCopilotAdditionalInstructions,
} from "@copilotkit/react-core";
import constants from "../../../../utils/constants";
import Store, { IStore } from "../../../../utils/Store";
import { useStoreState } from "pullstate";
import { MIXPANEL_EVENTS } from "../../../../utils/mixpanelEvents";
import { trackEvent } from "../../../../utils/mixpanel";
import { get } from "lodash";
import { useRouter } from "next/router";
import { useAuth } from "@qure/qure-auth";

const useChangeThemeWithAI = (isAIAssistantEnabled: boolean) => {
  const router = useRouter();
  const { isDarkMode } = useStoreState(Store, (store: IStore) => ({
    isDarkMode: store.isDarkMode,
  }));
  const THEME_OPTIONS = ["light", "dark"];
  const isViewerPage = router.pathname.includes("/viewer");

  useCopilotReadable({
    description: "The current theme",
    value: isDarkMode ? "dark" : "light",
    available: isAIAssistantEnabled ? "enabled" : "disabled",
  });

  const onToggleThemeClick = (_isDarkMode: boolean) => {
    if (window) {
      localStorage.setItem(constants.PERSISTED.isDarkMode, String(!isDarkMode));
      Store.update((s: IStore) => {
        s.isDarkMode = _isDarkMode;
      });
      trackEvent(MIXPANEL_EVENTS.THEME_CHANGED, {
        isDarkMode: _isDarkMode,
      });
    }
  };
  useCopilotAdditionalInstructions({
    instructions: `
      The 'changeTheme' action is used to change or toggle app theme when user specifies to change theme or mode, not any other property.
      Do not change the theme if user asks to go back to worklist or any other page.
      `,
    available: isAIAssistantEnabled ? "enabled" : "disabled",
  });

  useCopilotAction({
    name: "editReport1",
    available: isAIAssistantEnabled && !isViewerPage ? "enabled" : "disabled",
    description: `This action handles report editing requests. First check the patient context for the modality property.
      If modality is null or undefined, it means no scans are uploaded - inform the user that scans must be uploaded before editing the report.
      If modality has a value, inform the user they need to navigate to the viewer page and open the relevant series to edit the report.
      Report editing is only available from the viewer page.`,
    parameters: [],
    handler: async () => {
      return "Please navigate to the viewer page first to edit the report. Report editing is only available in the viewer.";
    },
  });
  1;
  useCopilotAction({
    name: "changeTheme",
    available: isAIAssistantEnabled ? "enabled" : "disabled",
    description: `Use this action when user wants to change or toggle app theme.
      If user specifies theme name, switch it to the specified theme or else switch it to the theme other than current theme.
      If user specifies theme name same as current theme, ask user if they want to switch to other theme.
      `,
    parameters: [
      {
        type: "string",
        name: "theme",
        enum: THEME_OPTIONS,
        required: true,
        description:
          "Toggle or switch between light and dark theme if user does not specify the theme.",
      },
    ],
    handler: async ({ theme }) => {
      const _isDarkMode = theme === "dark";
      trackEvent(MIXPANEL_EVENTS.ASSISTANT_ACTION, {
        action: "changeTheme",
        isDarkMode: _isDarkMode,
      });
      onToggleThemeClick(_isDarkMode);
    },
  });
};

const useChangeTheme = () => {
  const { userInfo } = useAuth();
  const isAIAssistantEnabled = get(
    userInfo,
    ["settings", "ui_settings", "ai_assistant"],
    false
  );

  return useChangeThemeWithAI(isAIAssistantEnabled);
};

export default useChangeTheme;
